import { Main as Container } from 'Components/Main';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { getUserById } from 'services/users-service';
import { getCompanyRolesBySlug } from 'services/company-service';
import { getAreasShortPaged } from 'services/areas-service';
import { getProcessesShortPaged } from 'services/processes-service';
import { getSystemsShortPaged } from 'services/systems-service';
import { getAssetsShortPaged } from 'services/assets-service';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import { PageHeader, Button } from 'Components/ui';
import styled from 'styled-components';

// ─── Avatar helpers ───────────────────────────────────────────────────────────

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))',
  'linear-gradient(135deg,var(--vo-success),var(--vo-capri))',
  'linear-gradient(135deg,var(--vo-warning),var(--vo-warning))',
  'linear-gradient(135deg,var(--vo-danger),var(--vo-warning))',
  'linear-gradient(135deg,var(--vo-celeste),var(--vo-cobalto))',
  'linear-gradient(135deg,var(--vo-purple-600),var(--vo-capri))',
  'linear-gradient(135deg,var(--vo-success),var(--vo-cobalto))',
  'linear-gradient(135deg,var(--vo-warning),var(--vo-danger))',
];
const getGradient = name =>
  AVATAR_GRADIENTS[(name || ' ').charCodeAt(0) % AVATAR_GRADIENTS.length];
const getInitials = name => {
  const parts = (name || '?').trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (name || '?').substring(0, 2).toUpperCase();
};

// ─── Criticality helper ───────────────────────────────────────────────────────

const CRITICITY_STYLE = {
  alta:  { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
  high:  { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
  média: { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  media: { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  medium:{ color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  baixa: { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  low:   { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
};
const getCriticityStyle = val => {
  if (!val) return { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' };
  return CRITICITY_STYLE[val.toLowerCase()] ?? { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' };
};

// ─── Date helper ─────────────────────────────────────────────────────────────

const formatDate = dateStr => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return null;
  }
};

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;


const TwoColGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

// Profile top section
const ProfileTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Rubik', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
`;

const UserName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: var(--vo-ink);
  margin: 0 0 4px;
  letter-spacing: -0.01em;
  text-align: center;
`;

const StatusBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? 'var(--vo-success)' : 'var(--vo-danger)')};
  background: ${({ $active }) =>
    $active ? 'var(--vo-success-10)' : 'var(--vo-danger-10)'};
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-block;
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FieldItem = styled.div``;

const FieldLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 4px;
`;

const FieldValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-ink);
  margin: 0;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const GroupTag = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-cobalto);
  background: var(--vo-cobalto-8);
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-block;
`;

// History card
const HistoryTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const HistoryStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HistoryValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
  margin: 0;
`;

// Association table card
const AssocCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;

const AssocHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid var(--vo-cloud);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AssocIconBox = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AssocTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

const AssocSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

const AssocTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const AssocTh = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 10px 16px;
  text-align: left;
  border-bottom: 1px solid var(--vo-cloud);
  background: var(--vo-mist);
`;

const AssocTr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`;

const AssocTd = styled.td`
  padding: 11px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink);
  font-weight: 500;
`;

const CriticityBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-block;
`;

const NoDataRow = styled.tr``;
const NoDataCell = styled.td`
  padding: 16px;
  text-align: center;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-steel);
`;

// Shimmer
const ShimmerLine = styled.div`
  height: ${({ $h }) => $h || '14px'};
  width: ${({ $w }) => $w || '100%'};
  border-radius: 6px;
  background: linear-gradient(90deg, var(--vo-mist) 25%, var(--vo-cloud) 50%, var(--vo-mist) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
`;

// ─── Sub-components ───────────────────────────────────────────────────────────

const AssocTableSection = ({ gradient, icon, title, subtitle, items, tKey, isLoading }) => {
  return (
    <AssocCard>
      <AssocHeader>
        <AssocIconBox $gradient={gradient}>{icon}</AssocIconBox>
        <div>
          <AssocTitle>{title}</AssocTitle>
          <AssocSubtitle>{subtitle}</AssocSubtitle>
        </div>
      </AssocHeader>
      <AssocTable>
        <thead>
          <tr>
            <AssocTh>{tKey('tableName')}</AssocTh>
            <AssocTh>{tKey('tableCriticity')}</AssocTh>
            <AssocTh>{tKey('tableStatus')}</AssocTh>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <AssocTr>
              <AssocTd><ShimmerLine $w="70%" /></AssocTd>
              <AssocTd><ShimmerLine $w="50px" /></AssocTd>
              <AssocTd><ShimmerLine $w="50px" /></AssocTd>
            </AssocTr>
          ) : !items || items.length === 0 ? (
            <NoDataRow>
              <NoDataCell colSpan={3}>{tKey('noData')}</NoDataCell>
            </NoDataRow>
          ) : (
            items.map(item => {
              const cs = getCriticityStyle(item.criticity);
              const isActive = !item.archived;
              return (
                <AssocTr key={item._id}>
                  <AssocTd>{item.name}</AssocTd>
                  <AssocTd>
                    {item.criticity ? (
                      <CriticityBadge $color={cs.color} $bg={cs.bg}>
                        {item.criticity}
                      </CriticityBadge>
                    ) : '—'}
                  </AssocTd>
                  <AssocTd>
                    <StatusBadge $active={isActive}>
                      {isActive ? tKey('active') : tKey('inactive')}
                    </StatusBadge>
                  </AssocTd>
                </AssocTr>
              );
            })
          )}
        </tbody>
      </AssocTable>
    </AssocCard>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const UsersShow = () => {
  const { t } = useTranslation(['user', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const selectedTenant = useSelector(useAuthTenant);

  const { isLoading, error, data: user } = useQuery(['getUserById', id], async () => {
    const res = await getUserById(id);
    return res.status === 200 ? res.data : null;
  });

  const { data: roles } = useQuery(['getCompanyRoles', selectedTenant.slug], async () => {
    if (!selectedTenant.slug) return [];
    const res = await getCompanyRolesBySlug(selectedTenant.slug);
    return res.status === 200 ? res.data : [];
  });

  const userFilter = user?.name ? `&user=${encodeURIComponent(user.name)}` : null;

  const { data: areasData, isLoading: areasLoading } = useQuery(
    ['userAreas', id, user?.name],
    async () => {
      const res = await getAreasShortPaged(selectedTenant.slug, 1, 100, userFilter);
      return res?.data?.data ?? res?.data ?? [];
    },
    { enabled: !!user?.name }
  );

  const { data: processesData, isLoading: processesLoading } = useQuery(
    ['userProcesses', id, user?.name],
    async () => {
      const res = await getProcessesShortPaged(selectedTenant.slug, 1, 100, userFilter);
      return res?.data?.data ?? res?.data ?? [];
    },
    { enabled: !!user?.name }
  );

  const { data: systemsData, isLoading: systemsLoading } = useQuery(
    ['userSystems', id, user?.name],
    async () => {
      const res = await getSystemsShortPaged(selectedTenant.slug, 1, 100, userFilter);
      return res?.data?.data ?? res?.data ?? [];
    },
    { enabled: !!user?.name }
  );

  const { data: assetsData, isLoading: assetsLoading } = useQuery(
    ['userAssets', id, user?.name],
    async () => {
      const res = await getAssetsShortPaged(selectedTenant.slug, 1, 100, userFilter);
      return res?.data?.data ?? res?.data ?? [];
    },
    { enabled: !!user?.name }
  );

  const userPermission = user?.permissions
    ?.filter(p => p.companyKey === selectedTenant.slug)
    .map(p => roles?.find(r => r.roleKey === p.roleKey)?.roleName)
    .filter(Boolean)
    .join(', ');

  const userGroups = user?.groups?.filter(g => g.companyKey === selectedTenant.slug) ?? [];

  const tShow = key => t(`user:users.show.${key}`);

  if (isLoading) {
    return (
      <Container>
        <PageWrap>
          <HeaderRow>
            <HeaderLeft>
              <ShimmerLine $h="12px" $w="180px" />
              <ShimmerLine $h="24px" $w="220px" style={{ marginTop: 6 }} />
            </HeaderLeft>
          </HeaderRow>
          <TwoColGrid>
            <LeftCol>
              <Card>
                <ProfileTop>
                  <ShimmerLine $h="64px" $w="64px" style={{ borderRadius: 16 }} />
                  <ShimmerLine $h="18px" $w="120px" />
                  <ShimmerLine $h="24px" $w="60px" />
                </ProfileTop>
                <FieldStack>
                  {[1, 2, 3, 4].map(i => (
                    <FieldItem key={i}>
                      <ShimmerLine $h="11px" $w="70px" style={{ marginBottom: 6 }} />
                      <ShimmerLine $h="14px" $w="80%" />
                    </FieldItem>
                  ))}
                </FieldStack>
              </Card>
            </LeftCol>
            <RightCol>
              {[1, 2].map(i => (
                <AssocCard key={i}>
                  <AssocHeader>
                    <ShimmerLine $h="28px" $w="28px" style={{ borderRadius: 7, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <ShimmerLine $h="14px" $w="40%" style={{ marginBottom: 4 }} />
                      <ShimmerLine $h="12px" $w="60%" />
                    </div>
                  </AssocHeader>
                </AssocCard>
              ))}
            </RightCol>
          </TwoColGrid>
        </PageWrap>
      </Container>
    );
  }

  if (error || !user) {
    return (
      <Container>
        <PageWrap>
          <p style={{ fontFamily: 'Lato', color: 'var(--vo-danger)' }}>{tShow('error')}</p>
        </PageWrap>
      </Container>
    );
  }

  return (
    <Container>
      <PageWrap>
        {/* Header */}
        <PageHeader
          title={user.name}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: t('user:users.title'), route: '/users' },
            { label: user.name },
          ]}
          actions={
            <Button
              variant="outline"
              onClick={() => navigate('/users/edit/' + id)}
              startIcon={
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              }
              sx={{ height: '40px', padding: '0 16px' }}
            >
              {tShow('edit')}
            </Button>
          }
        />

        <TwoColGrid>
          {/* ── Left column ── */}
          <LeftCol>
            {/* Profile card */}
            <Card>
              <ProfileTop>
                <Avatar $gradient={getGradient(user.name)}>
                  {getInitials(user.name)}
                </Avatar>
                <div style={{ textAlign: 'center' }}>
                  <UserName>{user.name}</UserName>
                  <StatusBadge $active={user.active}>
                    {user.active ? tShow('active') : tShow('inactive')}
                  </StatusBadge>
                </div>
              </ProfileTop>

              <FieldStack>
                <FieldItem>
                  <FieldLabel>{tShow('email')}</FieldLabel>
                  <FieldValue>{user.email || '—'}</FieldValue>
                </FieldItem>
                <FieldItem>
                  <FieldLabel>{tShow('phone')}</FieldLabel>
                  <FieldValue>{user.cellphone || '—'}</FieldValue>
                </FieldItem>
                <FieldItem>
                  <FieldLabel>{tShow('permission')}</FieldLabel>
                  <FieldValue style={{ fontWeight: 500 }}>{userPermission || '—'}</FieldValue>
                </FieldItem>
                {userGroups.length > 0 && (
                  <FieldItem>
                    <FieldLabel>{tShow('group')}</FieldLabel>
                    <TagsWrap>
                      {userGroups.map(g => (
                        <GroupTag key={g.name}>{g.name}</GroupTag>
                      ))}
                    </TagsWrap>
                  </FieldItem>
                )}
              </FieldStack>
            </Card>

            {/* History card */}
            <Card>
              <HistoryTitle>{tShow('historyTitle')}</HistoryTitle>
              <HistoryStack>
                <FieldItem>
                  <FieldLabel>{tShow('createdAt')}</FieldLabel>
                  <HistoryValue>{formatDate(user.createdAt) || tShow('noLastAccess')}</HistoryValue>
                </FieldItem>
                <FieldItem>
                  <FieldLabel>{tShow('lastAccess')}</FieldLabel>
                  <HistoryValue>{formatDate(user.lastAccess) || tShow('noLastAccess')}</HistoryValue>
                </FieldItem>
              </HistoryStack>
            </Card>
          </LeftCol>

          {/* ── Right column ── */}
          <RightCol>
            <AssocTableSection
              gradient="linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))"
              icon={
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              }
              title={tShow('areasTitle')}
              subtitle={t('user:users.show.areasSubtitle', { name: user.name })}
              items={areasData}
              tKey={tShow}
              isLoading={areasLoading}
            />

            <AssocTableSection
              gradient="linear-gradient(135deg,var(--vo-celeste),var(--vo-capri))"
              icon={
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 17 12 22 22 17" />
                  <polyline points="2 12 12 17 22 12" />
                </svg>
              }
              title={tShow('processesTitle')}
              subtitle={t('user:users.show.processesSubtitle', { name: user.name })}
              items={processesData}
              tKey={tShow}
              isLoading={processesLoading}
            />

            <AssocTableSection
              gradient="linear-gradient(135deg,var(--vo-success),var(--vo-capri))"
              icon={
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              }
              title={tShow('systemsTitle')}
              subtitle={tShow('systemsSubtitle')}
              items={systemsData}
              tKey={tShow}
              isLoading={systemsLoading}
            />

            <AssocTableSection
              gradient="linear-gradient(135deg,var(--vo-warning),var(--vo-warning))"
              icon={
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
              }
              title={tShow('assetsTitle')}
              subtitle={tShow('assetsSubtitle')}
              items={assetsData}
              tKey={tShow}
              isLoading={assetsLoading}
            />
          </RightCol>
        </TwoColGrid>
      </PageWrap>
    </Container>
  );
};
